import React, { useState } from 'react';
import { Line } from '@ant-design/charts';
import { Empty, Switch, Space, Typography } from 'antd'; // Import additional Ant Design components
import './styles/Chart.css';

const { Text } = Typography;

// Chart component now receives data, status, maxReviews, and optionally categoryData via props
const Chart = ({ chartData, loading, error, maxReviews, categoryData = [] }) => {
  
  // State to toggle between overall trends and category trends
  const [showCategoryTrends, setShowCategoryTrends] = useState(false);
  
  // Check if category trends are available (would need backend support for topic time-series)
  const categoriesAvailable = Array.isArray(categoryData) && categoryData.length > 0;
  
  // For now, we'll show a placeholder when category trends are enabled
  // In the future, this would use actual time-series data for each category
  const generateMockCategoryData = () => {
    if (!categoriesAvailable) return [];
    
    // Generate mock trend data for demonstration (replace with real data when backend supports it)
    const dates = ['2024-01-01', '2024-01-02', '2024-01-03', '2024-01-04', '2024-01-05'];
    const mockData = [];
    
    categoryData.slice(0, 4).forEach(category => { // Show top 4 categories
      dates.forEach(date => {
        // Generate realistic mock data based on the category's actual rating
        const baseRating = parseFloat(category.rating) || 3;
        const variation = (Math.random() - 0.5) * 0.8; // ±0.4 variation
        const value = Math.max(1, Math.min(5, baseRating + variation));
        
        mockData.push({
          date,
          value: parseFloat(value.toFixed(1)),
          category: category.name
        });
      });
    });
    
    return mockData;
  };
  
  // Determine which data to use for the chart
  const currentChartData = showCategoryTrends && categoriesAvailable 
    ? generateMockCategoryData() 
    : chartData; 

  // Ensure chartData is an array before filtering
  const validChartData = Array.isArray(currentChartData) ? currentChartData : [];

  // Filter out data points with null, undefined, or non-numeric/NaN values before passing to the chart
  const filteredChartData = validChartData.filter(
    d => d && typeof d.value === 'number' && !isNaN(d.value)
  );

  // Calculate max value from filtered data for yAxis configuration
  let maxValueFromData = 0;
  if (filteredChartData.length > 0) {
    const numericValues = filteredChartData.map(d => d.value);
    // Math.max on an empty array is -Infinity, so check numericValues.length
    if (numericValues.length > 0) {
      maxValueFromData = Math.max(...numericValues);
    }
  }

  // Ensure axis max is at least 1, and ceil to an integer for a clean scale
  const yAxisMax = Math.max(1, Math.ceil(maxValueFromData));

  // Dynamic colors for categories
  const getCategoryColors = () => {
    if (showCategoryTrends && categoriesAvailable) {
      return ['#094CB9', '#10B981', '#EF4444', '#F59E0B', '#8B5CF6', '#06B6D4'];
    }
    return ['#094CB9', '#f59e0b']; // Default colors for overall trends
  };

  const config = {
    data: filteredChartData, // Use pre-filtered and validated data
    xField: 'date',
    yField: 'value',
    seriesField: 'category',
    smooth: true,
    animation: {
      appear: {
        animation: 'path-in',
        duration: 1000,
      },
    },
    color: getCategoryColors(),
    lineStyle: ({ category }) => {
      if (showCategoryTrends) {
        return { opacity: 0.8 }; // All category lines have same style
      }
      // Original styling for overall trends
      if (category === 'Review Count') {
        return {
          lineDash: [4, 4],
          opacity: 0.7,
        };
      }
      return {
        opacity: 0.9,
      };
    },
    legend: {
      position: 'top',
    },    tooltip: {
      showMarkers: true,
      showContent: true,
      customContent: (title, items) => {
        if (!items || items.length === 0) {
          return `<div style="padding: 8px;"><div><strong>${title}</strong></div><div>No data for this point</div></div>`;
        }

        const content = items.map(item => {
          const data = item.data || {};
          let displayValue = 'N/A';
          
          if (showCategoryTrends) {
            // For category trends, show rating value with star
            if (typeof data.value === 'number') {
              displayValue = `${data.value.toFixed(1)}★`;
            }
          } else {
            // Original logic for overall trends
            if (data.category === 'Average Rating' && typeof data.value === 'number') {
              displayValue = `${data.value.toFixed(1)}★`;
            } else if (data.category === 'Review Count' && typeof data.value === 'number') {
              // Convert normalized value back to actual review count
              const actualMaxReviews = (typeof maxReviews === 'number' && maxReviews > 0) ? maxReviews : 1;
              const originalReviewCount = Math.round((data.value / 5) * actualMaxReviews);
              displayValue = `${originalReviewCount} ${originalReviewCount === 1 ? 'review' : 'reviews'}`;
            }
          }
          
          return `
            <div style="display: flex; align-items: center; margin-bottom: 4px;">
              <div style="width: 8px; height: 8px; border-radius: 50%; background-color: ${item.color}; margin-right: 8px;"></div>
              <div style="margin-right: 12px; white-space: nowrap;">${data.category || 'Unknown'}:</div>
              <div style="font-weight: 500;">${displayValue}</div>
            </div>
          `;
        }).join('');

        return `
          <div style="padding: 10px; background-color: white; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.15);">
            <div style="margin-bottom: 8px; font-weight: 600;">${title}</div>
            ${content}
          </div>
        `;
      },
    },
    yAxis: {
      title: {
        text: showCategoryTrends ? 'Rating Score (1-5)' : 'Scaled Value (Rating / Review Volume)',
        style: { fontSize: 12, fill: '#888' }
      },
      min: 0,
      max: showCategoryTrends ? 5 : yAxisMax, // Category trends use 1-5 scale
      tickInterval: showCategoryTrends ? 1 : 1,
      nice: false,
      label: {
        formatter: (v) => Number(v).toFixed(1),
        style: { fontSize: 10, fill: '#666' }
      },
      grid: {
        line: {
          style: {
            stroke: '#e5e5e5',
            lineDash: [2, 2]
          }
        }
      }
    },
    xAxis: {
      type: 'time',
      tickCount: 5,
      label: {
        style: { fontSize: 10, fill: '#666' }
      }
    },
    point: {
      size: 3,
      shape: 'circle',
      style: ({ category }) => {
        return {
          fill: category === 'Average Rating' ? '#094CB9' : '#f59e0b',
          stroke: category === 'Average Rating' ? '#2563eb' : '#d97706',
          lineWidth: 1.5,
        };
      },
    },
    loading: loading, 
    height: 300,
    padding: 'auto', 
  };

  // Render error message if error prop is set
  if (error) {
    return (
      <div className='chart-container'>
        <div className="chart-header">
          <h2 className="chart-title">
            {showCategoryTrends ? 'Category Performance Trends' : 'Ratings & Review Volume Trend'}
          </h2>
          {categoriesAvailable && (
            <div className="chart-controls">
              <Space align="center">
                <Text style={{ fontSize: '14px', color: '#6b7280' }}>
                  {showCategoryTrends ? 'Categories' : 'Overall'}
                </Text>
                <Switch 
                  checked={showCategoryTrends}
                  onChange={setShowCategoryTrends}
                  size="small"
                />
              </Space>
            </div>
          )}
        </div>
        <div className="chart-content">
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }

  // Render loading message if loading prop is true and data is empty
  if (loading && filteredChartData.length === 0 && !error) {
     return (
       <div className='chart-container'>
         <div className="chart-header">
           <h2 className="chart-title">
             {showCategoryTrends ? 'Category Performance Trends' : 'Ratings & Review Volume Trend'}
           </h2>
           {categoriesAvailable && (
             <div className="chart-controls">
               <Space align="center">
                 <Text style={{ fontSize: '14px', color: '#6b7280' }}>
                   {showCategoryTrends ? 'Categories' : 'Overall'}
                 </Text>
                 <Switch 
                   checked={showCategoryTrends}
                   onChange={setShowCategoryTrends}
                   size="small"
                 />
               </Space>
             </div>
           )}
         </div>
         <div className="chart-content">
           <div className="loading-message">Loading chart data...</div>
         </div>
       </div>
     );
  }
  
  // Render the chart using the config derived from props
  return (
    <div className='chart-container'>
      <div className="chart-header">
        <h2 className="chart-title">
          {showCategoryTrends ? 'Category Performance Trends' : 'Ratings & Review Volume Trend'}
        </h2>
        {categoriesAvailable && (
          <div className="chart-controls">
            <Space align="center">
              <Text style={{ fontSize: '14px', color: '#6b7280' }}>
                {showCategoryTrends ? 'Categories' : 'Overall'}
              </Text>
              <Switch 
                checked={showCategoryTrends}
                onChange={setShowCategoryTrends}
                size="small"
              />
            </Space>
          </div>
        )}
      </div>
      <div className="chart-content">
        {!loading && !error && filteredChartData.length > 0 && <Line {...config} />}
        {!loading && !error && filteredChartData.length === 0 && (
            <Empty description="No data available for the selected period to display the chart." />
        )}
        {showCategoryTrends && categoriesAvailable && (
          <div className="category-trends-notice">
            <Text style={{ fontSize: '12px', color: '#9ca3af', fontStyle: 'italic' }}>
              📊 Category trends are shown with sample data. Backend integration needed for actual time-series data.
            </Text>
          </div>
        )}
      </div>
    </div>
  );
};

export default Chart;